'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { MasonryProps } from './types';
import { BaseImage } from '../baseImage/BaseImage';
import clsx from 'clsx';
import Icon from '@/components/icon/Icon';

export const Masonry: React.FC<MasonryProps> = ({
  images,
  spacingTop,
  spacingBottom,
  imageDescription,
  sectionId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Define the number of columns
  const columnsCount = 4;
  const columns = Array.from(
    { length: columnsCount },
    () => [] as typeof images,
  );

  // Distribute images into columns
  images.forEach((image, index) => {
    columns[index % columnsCount].push(image);
  });

  const openModal = (columnIndex: number, imageIndex: number) => {
    // Calculate the global index of the clicked image
    const globalIndex = columnIndex + imageIndex * columnsCount;
    setSelectedIndex(globalIndex);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIndex(null);
  }, []);

  const handleNext = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % images.length); // Loop back to the first image
    }
  }, [images.length, selectedIndex]);

  const handlePrevious = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + images.length) % images.length); // Loop back to the last image
    }
  }, [images.length, selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isModalOpen) return;

      switch (event.key) {
        case 'ArrowRight':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'Escape':
          closeModal();
          break;
      }
    };

    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal, handleNext, handlePrevious, isModalOpen]);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  const selectedImageDescription = selectedImage?.imageDescription;
  const selectedBaseImageProps = selectedImage
    ? (({ imageDescription: _imageDescription, ...imageProps }) => imageProps)(
        selectedImage,
      )
    : null;

  return (
    <>
      <div
        id={sectionId}
        className={clsx(
          'container mx-auto grid grid-cols-2 gap-4 md:grid-cols-4',
          `${spacingTop ? 'pt-4 md:pt-8 lg:pt-20' : ''} ${spacingBottom ? 'pb-4 md:pb-8 lg:pb-20' : ''}`,
        )}
      >
        {columns.map((column, colIndex) => (
          <div
            className="grid grid-flow-row-dense auto-rows-min gap-4"
            key={colIndex}
          >
            {column.map((imgProps, imgIndex) => (
              <div
                key={imgIndex}
                onClick={() => openModal(colIndex, imgIndex)}
                className="cursor-pointer"
              >
                <BaseImage
                  {...imgProps}
                  sizes="(max-width: 768px) 50vw, 25vw"
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Modal for displaying the selected image */}
      {selectedImage && (
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          className="relative z-50"
        >
          {/* Overlay */}
          <div className="fixed inset-0 bg-[#031A29]" aria-hidden="true" />

          {/* Modal Content */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="relative h-[90vh] max-w-[54rem] rounded-lg p-4">
              <button
                onClick={closeModal}
                className="absolute left-[40px] top-[-1rem] flex gap-2 text-[22px] text-white focus:outline-none"
              >
                <Icon name="x" size={'32px'} color="white" /> Schliessen
              </button>
              <div className="mx-auto flex h-full w-full items-center justify-center">
                <button
                  onClick={handlePrevious}
                  className="absolute left-[-2rem] focus:outline-none md:left-[-5rem]"
                >
                  <Icon name="chevron-left" size={'90px'} color="white" />
                </button>
                <div className="md:d-block relative flex h-full max-h-[79vh] w-full max-w-[90%] flex-col justify-center">
                  {selectedBaseImageProps && (
                    <BaseImage
                      {...selectedBaseImageProps}
                      className="h-full w-full object-contain"
                      classNameImage="h-full w-full object-contain md:object-cover"
                      sizes="90vw"
                    />
                  )}
                  {selectedImageDescription && (
                    <p className="relative z-10 mt-[-88px] bg-[rgba(11,11,11,0.8)] p-8 text-white">
                      {selectedImageDescription}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleNext}
                  className="absolute right-[-2rem] focus:outline-none md:right-[-5rem]"
                >
                  <Icon name="chevron-right" size={'90px'} color="white" />
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </>
  );
};
